import { useUser } from "../user/user-provider";
import React, { createContext, useContext, useEffect } from "react";
import {
  CampaignDetails,
  CampaignMode,
  CampaignActionType,
  CampaignChannel,
} from "./models";
import { useBusiness } from "../business/provider";
import { campaignService } from "./service";
import { useRouter } from "next/router";
import { Timestamp } from "firebase/firestore/lite";
import { DbBusinessCampaign } from "./models";
import { DbBusinessService } from "../category-service/models";
import { clientService } from "../client/service";
import { filterService } from "./filter-service";
import { useCampaignFilter } from "./filter-provider";
import {
  useCampaignState,
  processFilteredClients,
  CampaignTemplate,
} from "./provider-state";
import { ExtendedTemplate } from "../whatsapp/message-templates/provider-state";
import { DbBusinessClient } from "../client/models";
import {
  filterClientsByEmail,
  mapToNotifiedUsers,
  prepareEmailCampaignData,
  prepareWhatsAppCampaignData,
  sendEmailTestCampaign as sendEmailTestCampaignHelper,
  sendWhatsAppTestCampaign as sendWhatsAppTestCampaignHelper,
  validateSendCampaign as validateSendCampaignHelper,
  sendEmailCampaignFlow as sendEmailCampaignFlowHelper,
  sendWhatsAppCampaignFlow as sendWhatsAppCampaignFlowHelper,
  isEmailTemplate,
} from "./provider-helpers";

interface CampaignContextProps {
  addCampaignCurrentStep: number;
  setAddCampaignCurrentStep: React.Dispatch<React.SetStateAction<number>>;
  editCampaignCurrentStep: number;
  setEditCampaignCurrentStep: React.Dispatch<React.SetStateAction<number>>;
  basicCampaignDetails: CampaignDetails;
  setBasicCampaignDetails: React.Dispatch<
    React.SetStateAction<CampaignDetails>
  >;
  templateDetails?: CampaignTemplate | null;
  setTemplateDetails: React.Dispatch<
    React.SetStateAction<CampaignTemplate | null>
  >;
  services: DbBusinessService[];
  setServices: React.Dispatch<React.SetStateAction<DbBusinessService[]>>;
  filterDetails?: any | null;
  setFilterDetails: React.Dispatch<React.SetStateAction<any>>;
  totalNotifiedClients: number;
  clients: DbBusinessClient[];
  setClients: React.Dispatch<React.SetStateAction<DbBusinessClient[]>>;
  testPhone?: string | null;
  setTestPhone?: React.Dispatch<React.SetStateAction<string | null>>;
  countryCode: string;
  setCountryCode: React.Dispatch<React.SetStateAction<string>>;
  testEmail?: string | null;
  setTestEmail?: React.Dispatch<React.SetStateAction<string | null>>;
  sendingTestCampaign?: boolean;
  setSendingTestCampaign?: React.Dispatch<React.SetStateAction<boolean>>;
  sendingCampaign?: boolean;
  setSendingCampaign?: React.Dispatch<React.SetStateAction<boolean>>;
  toastText?: string;
  setToastText?: React.Dispatch<React.SetStateAction<string>>;
  error: string | null;
  setError: React.Dispatch<React.SetStateAction<string | null>>;
  errors: { for: string; message: string }[];
  setErrors: React.Dispatch<
    React.SetStateAction<{ for: string; message: string }[]>
  >;
  step1Errors: { for: string; message: string }[];
  setStep1Errors: React.Dispatch<
    React.SetStateAction<{ for: string; message: string }[]>
  >;
  step2Errors: { for: string; message: string }[];
  setStep2Errors: React.Dispatch<
    React.SetStateAction<{ for: string; message: string }[]>
  >;
  step3Errors: { for: string; message: string }[];
  setStep3Errors: React.Dispatch<
    React.SetStateAction<{ for: string; message: string }[]>
  >;
  step4Errors: { for: string; message: string }[];
  setStep4Errors: React.Dispatch<
    React.SetStateAction<{ for: string; message: string }[]>
  >;
  sendTestCampaign: () => void;
  sendCampaign: () => void;
  createCampaign: () => void;
  updateCampaign: (id: string) => void;
  showSendCampaignModal: boolean;
  setShowSendCampaignModal: React.Dispatch<React.SetStateAction<boolean>>;
  whatsappCredits: number;
  setWhatsappCredits: React.Dispatch<React.SetStateAction<number>>;
  showBuyCreditModal: boolean;
  setShowBuyCreditModal: React.Dispatch<React.SetStateAction<boolean>>;
  isLoading: boolean;
  setIsLoading: React.Dispatch<React.SetStateAction<boolean>>;
  campaignSendingMode?: CampaignMode | null;
  setCampaignSendingMode: React.Dispatch<
    React.SetStateAction<CampaignMode | undefined>
  >;
  templates: CampaignTemplate[];
  setTemplates: React.Dispatch<React.SetStateAction<CampaignTemplate[]>>;
  actionType: CampaignActionType;
  setActionType: React.Dispatch<React.SetStateAction<CampaignActionType>>;
  campaign: DbBusinessCampaign | null;
  setCampaign: React.Dispatch<React.SetStateAction<DbBusinessCampaign | null>>;
  showingTemplates: boolean;
  setShowingTemplates: React.Dispatch<React.SetStateAction<boolean>>;
  isCopyingCampaign: boolean;
  setIsCopyingCampaign: React.Dispatch<React.SetStateAction<boolean>>;
  clearCampaignDetails: () => void;
  updateBasicCampaignField: (field: keyof CampaignDetails, value: any) => void;
}

const CampaignContext = createContext<CampaignContextProps | null>(null);

export default function CampaignProvider({
  children,
}: {
  children: React.ReactNode;
}) {
  const { user } = useUser();
  const router = useRouter();
  const { selectedBusiness, isSubscriptionActive } = useBusiness();
  const { filter, clearCampaignFilter } = useCampaignFilter();

  const state = useCampaignState();
  const {
    basicCampaignDetails,
    setBasicCampaignDetails,
    templateDetails,
    setTotalNotifiedClients,
    setClients,
    setError,
    setSendingTestCampaign,
    setSendingCampaign,
    setShowBuyCreditModal,
    setShowSendCampaignModal,
    setToastText,
    resetState,
  } = state;

  useEffect(() => {
    clearCampaignDetails();
    //eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedBusiness]);

  useEffect(() => {
    setBasicCampaignDetails((prev) => ({
      ...prev,
      filter,
    }));

    const fetchClients = async () => {
      if (!selectedBusiness) return;

      try {
        const channel =
          basicCampaignDetails.channel || CampaignChannel.WhatsApp;
        const isEmailCampaign = channel === CampaignChannel.Email;

        if (!filter) {
          let allClients = await clientService.getAllBusinessClients(
            selectedBusiness.id,
          );

          if (isEmailCampaign) {
            allClients = filterClientsByEmail(allClients);
          }

          setTotalNotifiedClients(allClients.length);
          setBasicCampaignDetails((prev) => ({
            ...prev,
            notifyingClients: allClients.map((client) => ({
              client_id: client.id,
              created_at: Timestamp.now(),
            })),
          }));

          const clientWithDetails = await processFilteredClients(
            selectedBusiness.id,
            allClients.map((client) => ({ client_id: client.id })),
          );
          const filteredClientDetails = isEmailCampaign
            ? filterClientsByEmail(clientWithDetails)
            : clientWithDetails;
          setClients(filteredClientDetails);
        } else {
          const result = await filterService.runCampaignFilter(
            filter,
            selectedBusiness.id,
            isEmailCampaign,
          );

          setTotalNotifiedClients(result.filteredClients.length);
          setBasicCampaignDetails((prev) => ({
            ...prev,
            notifyingClients: result.filteredClients,
          }));

          const clientWithDetails = await processFilteredClients(
            selectedBusiness.id,
            result.filteredClients,
          );
          const filteredClientDetails = isEmailCampaign
            ? filterClientsByEmail(clientWithDetails)
            : clientWithDetails;
          setClients(filteredClientDetails);
        }
      } catch (error) {
        setError(error as string | null);
      }
    };

    fetchClients();
    //eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filter, selectedBusiness, basicCampaignDetails.channel]);

  const prepareCampaignData = (): DbBusinessCampaign => {
    if (!selectedBusiness || !isSubscriptionActive || !user) {
      throw new Error("Business or user is not available.");
    }

    const channel = basicCampaignDetails.channel || CampaignChannel.WhatsApp;
    const isEmailCampaign = channel === CampaignChannel.Email;

    if (isEmailCampaign && isEmailTemplate(templateDetails)) {
      return prepareEmailCampaignData(basicCampaignDetails, templateDetails);
    }

    return prepareWhatsAppCampaignData(basicCampaignDetails, templateDetails);
  };

  const createCampaign = async () => {
    try {
      const data = prepareCampaignData();
      const isEmailCampaign = data.channel === CampaignChannel.Email;

      if (templateDetails) {
        if (
          basicCampaignDetails.notifyingClients &&
          basicCampaignDetails.notifyingClients.length > 0
        ) {
          data.notified_users = mapToNotifiedUsers(
            basicCampaignDetails.notifyingClients,
          );
        } else {
          if (!basicCampaignDetails.filter) {
            let clients = await clientService.getAllBusinessClients(
              selectedBusiness.id,
            );

            if (isEmailCampaign) {
              clients = filterClientsByEmail(clients);
            }

            data.notified_users = clients.map((client) => ({
              client_id: client.id,
              created_at: Timestamp.now(),
            }));
          }
        }
      }

      data.created_at = Timestamp.now();
      const newCampaign = await campaignService.createCampaign(
        selectedBusiness.id,
        data,
      );

      setBasicCampaignDetails({ ...basicCampaignDetails, id: newCampaign.id });
    } catch (error: any) {
      setError(error);
    }
  };

  const updateCampaign = async (id: string) => {
    try {
      const data = prepareCampaignData();
      data.id = id;
      const isEmailCampaign = data.channel === CampaignChannel.Email;

      if (templateDetails) {
        if (!basicCampaignDetails.filter) {
          let clients = await clientService.getAllBusinessClients(
            selectedBusiness.id,
          );

          if (isEmailCampaign) {
            clients = clients.filter(
              (client) => client.email && client.email.trim() !== "",
            );
          }

          data.notified_users = clients.map((client) => ({
            client_id: client.id,
            created_at: Timestamp.now(),
          }));
        } else {
          if (
            basicCampaignDetails.notifyingClients &&
            basicCampaignDetails.notifyingClients.length > 0
          ) {
            data.notified_users = basicCampaignDetails.notifyingClients.map(
              (client: any) => ({
                client_id: client.client_id || client.client_id,
                created_at: Timestamp.now(),
              }),
            );
          }
        }
      }

      await campaignService.updateCampaign(selectedBusiness.id, data);
      setBasicCampaignDetails({ ...basicCampaignDetails, id });
    } catch (error: any) {
      setError(error);
    }
  };

  const sendTestCampaign = async () => {
    if (!templateDetails || !selectedBusiness) return;

    const channel = basicCampaignDetails.channel || CampaignChannel.WhatsApp;
    const isEmailCampaign = channel === CampaignChannel.Email;

    setSendingTestCampaign(true);

    try {
      if (isEmailCampaign) {
        await sendEmailTestCampaignHelper(
          selectedBusiness,
          templateDetails,
          basicCampaignDetails,
          state.testEmail,
          setToastText,
        );
      } else {
        const fullTestPhone = state.testPhone
          ? `${state.countryCode}${state.testPhone}`
          : state.testPhone;

        await sendWhatsAppTestCampaignHelper(
          selectedBusiness,
          templateDetails,
          basicCampaignDetails,
          fullTestPhone,
          state.whatsappCredits,
          setShowBuyCreditModal,
        );
      }
    } catch (error: any) {
      setError(error);
    } finally {
      setSendingTestCampaign(false);
    }
  };

  const sendCampaign = async () => {
    const channel = basicCampaignDetails.channel || CampaignChannel.WhatsApp;
    const isEmailCampaign = channel === CampaignChannel.Email;

    if (
      !validateSendCampaignHelper(
        selectedBusiness,
        templateDetails,
        basicCampaignDetails,
        isEmailCampaign,
      )
    ) {
      return;
    }

    try {
      setSendingCampaign(true);

      const clientIds = basicCampaignDetails.notifyingClients.map(
        (client) => client.client_id || (client as { id?: string }).id || "",
      );

      const clients = await clientService.getBusinessClientsByIds(
        selectedBusiness.id,
        clientIds,
      );

      if (isEmailCampaign) {
        await sendEmailCampaignFlowHelper(
          selectedBusiness,
          templateDetails!,
          basicCampaignDetails,
          clients,
          router,
          setShowSendCampaignModal,
          clearCampaignDetails,
        );
      } else {
        await sendWhatsAppCampaignFlowHelper(
          selectedBusiness,
          templateDetails!,
          basicCampaignDetails,
          clients,
          state.whatsappCredits,
          router,
          setShowSendCampaignModal,
          setShowBuyCreditModal,
          clearCampaignDetails,
        );
      }
    } catch (error: any) {
      setError(error);
    } finally {
      setSendingCampaign(false);
    }
  };

  const clearCampaignDetails = () => {
    resetState();
    clearCampaignFilter();
  };

  return (
    <CampaignContext.Provider
      value={{
        ...state,
        sendTestCampaign,
        sendCampaign,
        createCampaign,
        updateCampaign,
        clearCampaignDetails,
      }}
    >
      {children}
    </CampaignContext.Provider>
  );
}

export const useCampaign = () => {
  const context = useContext(CampaignContext);
  if (!context) {
    throw new Error("useCampaign must be used within CampaignProvider");
  }
  return context;
};
